// @ts-nocheck
import { v4 as uuid } from 'uuid';
import paper from 'paper';
import { ImageSharedModels, AnnotationEntityModels } from '@medsurf/flat-models';
import { Selection } from '../marker/selection';
import { End } from '../marker/pointer/end/end';
import { Label } from '../marker/label/label';
import { Pointer } from '../marker/pointer/pointer';
import { Line } from '../marker/pointer/line';
import { Drawable } from "../drawable";

export class InteractiveArea extends Drawable<ImageSharedModels.FreeformModel> {
  /**
   * Members
   */
  public _selftests: any[];
  public _state: any;
  private _segments: any[];
  private _labels: any[];
  private _pointers: any[];
  private _selection: any;
  private _forkLine: any;
  private _endType: any;
  private _end: End;

  /**
   * Constructor
   *
   * @param _model
   * @param _localPaper
   * @param _format
   * @param _marker
   */
  public constructor(_model: ImageSharedModels.FreeformModel,
                     private _localPaper,
                     private _format,
                     _marker) {
    super(_model);
    this._marker = _marker;
    this._selftests = [];
    this._state = undefined;

    this._element = null;
    this._segments = [];
    this._labels = [];
    this._pointers = [];
    this._selection = null;
    this._forkLine = null;
    if (this._model.target) {
      this._endType = this._model.target.endType;
    }
    this._init();
  }

  /**
   * Init
   *
   * @protected
   */
  protected _init() {
    this.draw();
  }

  /**
   * Draw
   */
  public draw() {
    this.cleanUp();
    this.drawPath();
    if (this._segments.length > 0) {
      if (this._element.freeform._labelVisible && (this._model.label && !this._model.label.hidden)) {
        this.drawPointers();
        this.drawForkLine();
        this.drawLabels();
      }
      this.drawSelection();
    }
    this.arrange();
  }

  /**
   * Draw Path
   */
  public drawPath() {
    if (this._model.annotation.path && this._model.annotation.path.length > 1) {
      for (const pathPoint of this._model.annotation.path) {
        this._segments.push(new paper.Point(this._marker.container.getAbsoluteCoords(
          pathPoint.x,
          pathPoint.y,
          this._marker._imageOffset,
          this._marker._imageScale
        )));
      }
    } else {
      return;
    }

    this._element = new paper.Path(this._segments);

    this._element.strokeColor = this._model.freeFormStyle.strokeColor || this._model.freeFormStyle.color || this._format.border.strokeColor;
    this._element.strokeWidth = (this._model.freeFormStyle.strokeWidth || this._format.strokeWidth) / this._marker._imageScale;
    this._element.dashArray = this._model.freeFormStyle.dash ?
      this._model.freeFormStyle.dash
        .split(',')
        .map((dash) => {
          return <any>dash * (this._marker && this._marker._model.freeFormStyle && this._marker._model.freeFormStyle.strokeWidth ?
              this._marker._model.freeFormStyle.strokeWidth :
              1
          );
        }) :
      [1, 0];
    this._element._model = this._model;

    if (this._model.freeFormStyle.closePath) {
      this._element.closed = true;
      this._element.fillColor = this._model.freeFormStyle.background ?
        (this._model.freeFormStyle.backgroundInBorderColor ?
          this._model.freeFormStyle.strokeColor || this._model.freeFormStyle.color || this._format.border.strokeColor :
          this._model.freeFormStyle.color || this._format.border.fillColor) :
        undefined;
      if (this._element.fillColor) {
        this._element.fillColor.alpha = this._model.freeFormStyle.opacity;
      }
    }

    if (this._model.freeFormStyle.smooth) {
      this._element.smooth({
        type: 'geometric',
        factor: this._model.freeFormStyle.smoothFactor ?? 0
      });
    }

    this._element.freeform = this._marker;
    this._marker._element.addChild(this._element);
  }

  /**
   * Draw Selection
   */
  public drawSelection() {
    /*
    const factor = Math.max(1 / (this._marker.container._localPaper.view.zoom / 2), 1);
    const radius = factor * this._format.selection.radius;
    
    this._segments.forEach((center) => {
      const selection = new paper.Path.Circle(
        center,
        radius
      );
      selection.strokeColor = this._format.selection.color;
      selection.fillColor = this._format.selection.color;
      selection.fillColor.alpha = this._format.selection.opacity;
      selection.strokeWidth = radius / 10;
      selection.freeform = this._marker;
      selection.positionInPath = this._segments.indexOf(center);
      selection.data = {
        'selectionType': 'segment'
      };
      selection.bringToFront();
      this._marker._element.addChild(selection);
    });
    */
    if (this._selection) {
      this.removeSelection();
    }
    this._selection = new Selection(this._model, this._format, this._localPaper, this._marker.container, this);
    this._marker._element.addChild(this._selection.element);
  }

  /**
   * Draw Pointers
   */
  public drawPointers() {
    let source = this._marker.container.getAbsoluteCoords(
      this._model.source.position.x,
      this._model.source.position.y,
      this._marker._imageOffset,
      this._marker._imageScale
    );
    if (this._model.annotation.fork.x && this._model.annotation.fork.y) {
      source = this._marker.container.getAbsoluteCoords(
        this._model.annotation.fork.x,
        this._model.annotation.fork.y,
        this._marker._imageOffset,
        this._marker._imageScale
      );
    }
    if (this._model.freeFormStyle.closePath && this._element.contains(source)) {
      return;
    }
    const target = this._element.getNearestPoint(new paper.Point(source));
    if (!this._model.target) {
      this._model.target = {
        endType: AnnotationEntityModels.TipType.enum.none,
        position: {
          x: 0.5,
          y: 0.5
        }
      };
    }
    const pointer = new Pointer(
      (this._model.target && this._model.target.endType) ? (this._model.target.endType) : 'none',
      this._format,
      source,
      target,
      this._marker
    );
    this._marker._element.addChild(pointer.end.element.element);
    this._marker._element.addChild(pointer.line.element);
    this._pointers.push(pointer);
  }

  /**
   * Draw Fork Line
   */
  public drawForkLine() {
    if (this._model.annotation.fork.x && this._model.annotation.fork.y) {
      if (this._forkLine) {
        this.removeForkLine();
      }
      const source = this._marker.container.getAbsoluteCoords(
        this._model.source.position.x,
        this._model.source.position.y,
        this._marker._imageOffset,
        this._marker._imageScale
      );
      const target = this._marker.container.getAbsoluteCoords(
        this._model.annotation.fork.x,
        this._model.annotation.fork.y,
        this._marker._imageOffset,
        this._marker._imageScale
      );
      this._forkLine = new Line(this._format, source, target, this._marker);
      this._forkLine.element.marker = this;
      this._marker._element.addChild(this._forkLine.element);
    }
  }

  /**
   * Draw Labels
   */
  public drawLabels() {
    if (!this._model.label) {
      return;
    }

    const label = new Label({id: uuid(), text: this._model.label.text}, this._format, this._marker);
    this._marker._element.addChild(label.element);
    this._labels.push(label);
  }

  /**
   * Getter selftest
   */
  public get selftest() {
    return this._selftests ? this._selftests : null;
  }

  /**
   * Getter element
   */
  public get element() {
    return this._element;
  }

  /**
   * Set Scale
   */
  public setScale() {
    this.element.strokeWidth = this._format.strokeWidth;
  }

  /**
   * Remove Labels
   */
  public removeLabels() {
    for (let label of this._labels) {
      label.cleanUp();
      label = null;
    }
    this._labels = [];
  }

  /**
   * Remove Pointers
   */
  public removePointers() {
    for (let pointer of this._pointers) {
      pointer.cleanUp();
      pointer = null;
    }
    this._pointers = [];
  }

  /**
   * Remove Selection
   */
  public removeSelection() {
    if (this._selection) {
      this._selection.cleanUp();
      this._selection = null;
    }
  }

  /**
   * Remove Fork Line
   */
  public removeForkLine() {
    if (this._forkLine) {
      this._forkLine.cleanUp();
      this._forkLine = null;
    }
  }

  /**
   * Clean Up
   */
  public cleanUp() {
    this.removeSelection();
    this.removeLabels();
    this.removePointers();
    this.removeForkLine();

    if (this._element) {
      this._element.remove();
      this._element = null;
      this._segments = [];
    }
  }

  /**
   * Resize
   *
   * @param w
   * @param h
   */
  public resize(w: number,
                h: number) {
    return;
  }

  /**
   * Arrange
   */
  public arrange() {
    const zoom = this._localPaper.view.zoom;
    const coords = this._marker.container.getAbsoluteCoords(
      this._model.source.position.x,
      this._model.source.position.y,
      this._marker._imageOffset,
      this._marker._imageScale
    );
    let forkCoords = coords;
    this.scale(zoom);
    if (this._labels.length > 0) {
      this._labels[0].setPosition(coords.x, coords.y);
    }
    if (this._forkLine) {
      this._forkLine.setScale(zoom);
    }
    if (this._marker._isSelected) {
      if (this._selection && this._selection.rectangle) {
        if (this._model.annotation.fork) {
          forkCoords = this._marker.container.getAbsoluteCoords(
            this._model.annotation.fork.x || this._model.source.position.x,
            this._model.annotation.fork.y || this._model.source.position.y,
            this._marker._imageOffset,
            this._marker._imageScale
          );
        }
        this._selection.setScale(zoom);
        this._selection.rectangle.position = coords;
        if (this._selection.circle) {
          this._selection.circle.position = forkCoords;
        }
        this._selection.arrange();
        this._selection.element.bringToFront();
      }
    } else {
      this.removeSelection();
    }
  }

  /**
   * Scale
   *
   * @param zoom
   */
  public scale(zoom) {
    if (this._labels.length > 0) {
      this._labels[0].setScale(zoom);
    }
    for (const pointer of this._pointers) {
      pointer.setScale(zoom);
    }
  }
}
